/**
 * Dead-Reckoning Log Component
 *
 * DM-only panel that surfaces the navigation state machine and a chronological
 * log of the ship's true movement — including the drift the players can't see
 * while they're lost. Reads gameState.navigation + gameState.navLog.
 *
 * No Owlbear Rodeo dependency: this is pure dashboard data. The OBR extension
 * later reads/writes the same navigation state, but the log stands on its own.
 */

import React, { useState } from 'react';
import { NavigationState, NavLogEntry, NavStatus, NavEventType, HexCoord } from '../types/navigation-types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../@/components/ui/card';
import { Badge } from '../@/components/ui/badge';
import { Button } from '../@/components/ui/button';
import { Label } from '../@/components/ui/label';
import { useSession } from '../session-context';
import { arrive } from '../utils/nav-utils';

interface DeadReckoningLogProps {
  navigation: NavigationState;
  navLog: NavLogEntry[];
}

const STATUS_META: Record<NavStatus, { label: string; description: string; className: string }> = {
  'not-charted': {
    label: 'Not Charted',
    description: 'No course has been set. Chart a course to begin the voyage.',
    className: 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300'
  },
  'on-course': {
    label: 'On Course',
    description: 'Believed position matches the truth. The ship sails true.',
    className: 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
  },
  'lost-unaware': {
    label: 'Lost — Crew Unaware',
    description: 'The ship has drifted off course, but the crew still believes they are on the plotted line. The map is lying to them.',
    className: 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300'
  },
  'lost-aware': {
    label: 'Lost — Crew Aware',
    description: 'The crew has realized they are lost. The believed token is hidden until they re-chart or make landfall.',
    className: 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800 text-red-700 dark:text-red-300'
  },
  'arrived': {
    label: 'Arrived',
    description: 'The ship reached a recognizable hex. Believed position has snapped to the truth.',
    className: 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300'
  }
};

const EVENT_ICON: Record<NavEventType, string> = {
  'chart-course': '🧭',
  'advance': '➡️',
  'became-lost': '🌫️',
  'drift': '🌀',
  'recognition-check': '👁️',
  'recognized': '⚠️',
  'rechart': '🧭',
  'arrived': '⚓',
  'manual-adjust': '✏️'
};

const EVENT_LABEL: Record<NavEventType, string> = {
  'chart-course': 'Chart Course',
  'advance': 'Advance',
  'became-lost': 'Became Lost',
  'drift': 'Drift',
  'recognition-check': 'Recognition Check',
  'recognized': 'Recognized Lost',
  'rechart': 'Re-chart',
  'arrived': 'Arrived',
  'manual-adjust': 'Manual Adjust'
};

const getEventBadgeVariant = (eventType: NavEventType) => {
  switch (eventType) {
    case 'became-lost':
    case 'drift':
    case 'recognized':
      return 'destructive' as const;
    case 'arrived':
    case 'advance':
      return 'default' as const;
    default:
      return 'outline' as const;
  }
};

const formatHex = (hex?: HexCoord): string => (hex ? `${hex.q}, ${hex.r}` : '—');

const formatTimestamp = (timestamp: number): string => new Date(timestamp).toLocaleString();

export const DeadReckoningLog: React.FC<DeadReckoningLogProps> = ({ navigation, navLog }) => {
  const { isDM, gameState, updateNavigation, addNavLogEntries } = useSession();
  const [filterEvent, setFilterEvent] = useState('');

  const canArrive = isDM() && navigation.status !== 'not-charted' && navigation.status !== 'arrived';

  const handleArrive = async () => {
    const period = gameState?.voyageDay ?? 0;
    const { nav, logs } = arrive(navigation, {
      period,
      destinationName: gameState?.voyageDestination?.name
    });
    await updateNavigation(nav);
    await addNavLogEntries(logs);
  };

  // Truth-bearing rows are DM-only; filter for non-DM viewers (defensive — this
  // panel lives in the DM tab, but the filter keeps it safe if reused elsewhere).
  const visibleLog = isDM() ? navLog : navLog.filter(e => !e.isDMOnly);

  const sortedLog = [...visibleLog].sort((a, b) => b.timestamp - a.timestamp);
  const filteredLog = filterEvent
    ? sortedLog.filter(e => e.eventType === filterEvent)
    : sortedLog;

  const uniqueEvents = Array.from(new Set(visibleLog.map(e => e.eventType)));
  const status = STATUS_META[navigation.status];
  const isLost = navigation.status === 'lost-unaware' || navigation.status === 'lost-aware';
  const recognitionDC = Math.max(1, 20 - Math.floor(navigation.daysOfRandomTravel));

  return (
    <div className="space-y-6">
      {/* Navigation status summary */}
      <Card>
        <CardHeader>
          <CardTitle>Navigation Status</CardTitle>
          <CardDescription>The ship's true state — visible to the DM only.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`p-4 rounded border ${status.className}`}>
            <div className="text-lg font-bold">{status.label}</div>
            <p className="text-sm mt-1">{status.description}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">Believed position (players see)</div>
              <div className="text-lg font-mono font-semibold">
                {navigation.believed.display === 'hidden' ? 'hidden' : formatHex(navigation.believed.hex)}
              </div>
            </div>
            <div className="p-3 rounded border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10">
              <div className="text-xs text-red-500 dark:text-red-400">True position (DM only)</div>
              <div className="text-lg font-mono font-semibold text-red-700 dark:text-red-300">
                {formatHex(navigation.truth.hex)}
              </div>
            </div>
          </div>

          {isLost && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Days of random travel</div>
                <div className="text-lg font-semibold">{navigation.daysOfRandomTravel}</div>
              </div>
              <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400">Recognition DC (Know. Geography)</div>
                <div className="text-lg font-semibold">{recognitionDC}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">DC 20 − {Math.floor(navigation.daysOfRandomTravel)} days</div>
              </div>
            </div>
          )}

          {canArrive && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={handleArrive} className="w-full">
                ⚓ Make Landfall{gameState?.voyageDestination?.name ? ` at ${gameState.voyageDestination.name}` : ''}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                Marks arrival: the believed position snaps to the truth and the crew knows where they are again.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* The log */}
      <Card>
        <CardHeader>
          <CardTitle>Dead-Reckoning Log</CardTitle>
          <CardDescription>
            Chronological record of the ship's true movement ({visibleLog.length} {visibleLog.length === 1 ? 'entry' : 'entries'})
          </CardDescription>

          {visibleLog.length > 0 && (
            <div className="mt-4 max-w-xs">
              <Label htmlFor="filter-nav-event">Filter by Event</Label>
              <select
                id="filter-nav-event"
                className="w-full p-2 border rounded dark:bg-gray-800 text-sm"
                value={filterEvent}
                onChange={(e) => setFilterEvent(e.target.value)}
              >
                <option value="">All Events</option>
                {uniqueEvents.map(evt => (
                  <option key={evt} value={evt}>{EVENT_LABEL[evt]}</option>
                ))}
              </select>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {filteredLog.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                {visibleLog.length === 0
                  ? 'No navigation activity yet. Chart a course to begin tracking the ship\'s movement.'
                  : 'No entries match your filter.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLog.map(entry => (
                <div
                  key={entry.id}
                  className="p-4 border rounded dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xl">{EVENT_ICON[entry.eventType]}</span>
                        <span className="font-semibold">{EVENT_LABEL[entry.eventType]}</span>
                        <Badge variant={getEventBadgeVariant(entry.eventType)} className="text-xs">
                          {entry.eventType}
                        </Badge>
                        <Badge variant="outline" className="text-xs">Day {entry.periodNumber}</Badge>
                        {entry.isDMOnly && (
                          <Badge variant="destructive" className="text-xs">DM only</Badge>
                        )}
                      </div>

                      {/* Movement details */}
                      {(entry.direction !== undefined || entry.distanceHexes !== undefined || entry.truthHex || entry.believedHex) && (
                        <div className="flex items-center gap-4 mb-2 text-sm text-gray-600 dark:text-gray-400 flex-wrap">
                          {(entry.compassLabel || entry.direction !== undefined) && (
                            <span>Heading: <strong>{entry.compassLabel ?? `dir ${entry.direction}`}</strong></span>
                          )}
                          {entry.distanceHexes !== undefined && (
                            <span>Distance: <strong>{entry.distanceHexes} hex{entry.distanceHexes !== 1 ? 'es' : ''}</strong></span>
                          )}
                          {entry.truthHex && (
                            <span className="text-red-600 dark:text-red-400 font-mono">truth → {formatHex(entry.truthHex)}</span>
                          )}
                          {entry.believedHex && (
                            <span className="font-mono">believed → {formatHex(entry.believedHex)}</span>
                          )}
                        </div>
                      )}

                      {/* Check result */}
                      {entry.check && (
                        <div className="flex items-center gap-2 mb-2 text-sm">
                          <span className="text-gray-600 dark:text-gray-400">{entry.check.skill} vs DC {entry.check.dc}:</span>
                          <span className="font-semibold">{entry.check.roll}</span>
                          <Badge variant={entry.check.success ? 'default' : 'destructive'} className="text-xs">
                            {entry.check.success ? 'success' : 'failure'}
                          </Badge>
                        </div>
                      )}

                      {/* Reason */}
                      <p className="text-sm text-gray-700 dark:text-gray-300">{entry.reason}</p>

                      {entry.daysOfRandomTravel !== undefined && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          {entry.daysOfRandomTravel} day{entry.daysOfRandomTravel !== 1 ? 's' : ''} of random travel
                        </p>
                      )}
                    </div>

                    <div className="text-xs text-gray-500 dark:text-gray-400 text-right whitespace-nowrap">
                      {formatTimestamp(entry.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
